import { useDebounceFn, useMount, useUnmount } from 'ahooks';
import React, { memo } from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import parameters from '../../../../config/parameters';
import { PropsStore } from '../../../../store/propsStore/propsStore';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { getActiveDescendantName } from './getDomProps';
import SearchLabel from './SearchLabel';
import { TTreeContext, TreeContextProvider } from './TreeContext';
import { useTreeSelector } from './TreeDataController';
import TreeItemChildren from './TreeItemChildren';
import { TDataNode, TId, TTreeDataControllerState, TTreeProps } from './types';
import useTreeData from './useTreeData';
import { unfilterNodes, matchNodesAgainstString } from './util';

const Tree = <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  props: TTreeProps<NodeProps, NodeType>,
) => {
  const { data, handler, keyHandler } = useTreeData<NodeProps, NodeType>({
    name: props.name,
    treeProps: props,
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [currentSearchString, setCurrentSearchString] = React.useState('');
  const isTreeLoading = useTreeSelector(handler, {
    selector: (current) => current.isLoading,
  });
  React.useEffect(() => {
    setIsLoading(isTreeLoading);
  }, [isTreeLoading]);

  const search = useDebounceFn(
    () => {
      if (props.filterString === undefined) return;
      setIsLoading(true);
      React.startTransition(() => {
        setCurrentSearchString(props.filterString as string);
      });
      if (props.filterString !== undefined && props.filterString !== '') {
        unfilterNodes(handler);
        matchNodesAgainstString(handler, props.filterString);
        handler.forceEmitUpdate();
      } else {
        unfilterNodes(handler);
        handler.forceEmitUpdate();
      }
      setTimeout(() => setIsLoading(false), 0);
    },
    { wait: parameters.timing.keyboardFilterDebounceTime },
  );

  React.useEffect(() => {
    search.run();
    if (!props.filterString) search.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterString]);

  usePropsSelector<TTreeDataControllerState, TTreeDataControllerState>(
    handler.stateKey,
    {
      propsStore:
        handler.propsStore as unknown as PropsStore<TTreeDataControllerState>,
      selector: (current) => current,
      comparator: (prevProps, newProps) => {
        if (
          props.onSelect &&
          !shallowEqual(prevProps?.selectedNodes, newProps?.selectedNodes)
        ) {
          props.onSelect(
            (newProps?.selectedNodes ?? []).map((currentId) =>
              handler.propsStore.getFieldProps(currentId),
            ),
          );
        }
        return true;
      },
    },
  );

  const { focusedNode } = usePropsSelector<
    { focusedNode: TId | null },
    TTreeDataControllerState
  >(handler.stateKey, {
    propsStore:
      handler.propsStore as unknown as PropsStore<TTreeDataControllerState>,
    selector: (current) => ({
      focusedNode: current.focusedNode,
    }),
  });

  useMount(() => {
    if (props.getHandler) props.getHandler(handler);
  });

  useUnmount(() => handler.destructor());

  const contextValue = React.useMemo(() => {
    const actualValue: TTreeContext<NodeProps, NodeType> = {
      handler,
      name: props.name,
      forceUpdate: data.forceUpdate,
      focusGetter: props.focusGetter,
      treeProps: props,
    };
    return actualValue;
  }, [handler, props, data.forceUpdate]);

  return (
    <TreeContextProvider value={contextValue}>
      <Box
        className={`tree ${props.className ?? ''}`}
        {...window.getVariant(
          props.variant ?? 'layout.common.components.trees.primary',
        )}
      >
        <SearchLabel
          isLoading={props.isLoading || isLoading}
          onDelete={props.onDeleteFilterString}
          searchString={props.hideSearchLabel ? '' : currentSearchString}
        />
        <TreeItemChildren
          aria-activedescendant={
            focusedNode !== null
              ? getActiveDescendantName(props.name, focusedNode)
              : undefined
          }
          aria-label={props.label}
          as="ul"
          forceUpdate={data.forceUpdate}
          level={-1}
          nodes={React.useMemo(
            () =>
              data.children.map((current) =>
                handler.propsStore.getFieldProps(current),
              ),
            [data.children, handler.propsStore],
          )}
          role="tree"
          tabIndex={0}
          {...keyHandler}
          {...props.treeBoxProps}
        />
      </Box>
    </TreeContextProvider>
  );
};

export default memo(Tree) as typeof Tree;
